import { api_gateway } from '../../conexoes/api'
import { formatBusca } from '../../utils/genericFormatters'
import { requestResolver } from '../../utils/requestResolver'

const getParams = (busca, separadorLinha) => {
  if (!busca) return ''
  return formatBusca(busca, separadorLinha)
}

function transformData(data) {
  const finalFiltros = Object.fromEntries(
    Object.entries(data)
      .filter(([, v]) => v && (!Array.isArray(v) || v.length > 0))
      .map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : v])
  )

  return finalFiltros
}

export const ESimsService = {
  //#region GET
  getESims: (
    pagina,
    linhasPorPagina,
    filtros = {},
    busca,
    separadorLinhaBusca,
    contagem
  ) => {
    const params = {
      params: {
        page: pagina,
        pageRows: linhasPorPagina,
        count: contagem,
        filter: filtros.vazio ? undefined : filtros,
        fetch: busca ? getParams(busca, separadorLinhaBusca) : undefined
      }
    }

    Object.keys(params.params).forEach(
      (key) => params.params[key] === undefined && delete params.params[key]
    )

    return requestResolver(
      api_gateway.get(`/api/v2/embeddedSim/simcard`, params)
    )
  },
  getProfiles: (
    pagina,
    linhasPorPagina,
    filtros = {},
    busca,
    separadorLinhaBusca,
    contagem
  ) => {
    const params = {
      params: {
        page: pagina,
        pageRows: linhasPorPagina,
        count: contagem,
        filter: filtros.vazio
          ? undefined
          : {
              ...filtros,
              ...(filtros.PROVISIONED !== undefined && {
                PROVISIONED: filtros.PROVISIONED == '1' ? true : false
              })
            },
        fetch: busca ? getParams(busca, separadorLinhaBusca) : undefined
      }
    }

    Object.keys(params.params).forEach(
      (key) => params.params[key] === undefined && delete params.params[key]
    )

    return requestResolver(
      api_gateway.get(`/api/v2/embeddedSim/fetchInventory`, params)
    )
  },
  fetchFavActions: (manejador) => {
    return requestResolver(
      api_gateway.get('/api/v2/favorites', {
        params: { acao: manejador }
      })
    )
  },
  getRequests: (
    pagina,
    linhasPorPagina,
    filtros = {},
    busca,
    separadorLinhaBusca,
    contagem
  ) => {
    const params = {
      params: {
        page: pagina,
        pageRows: linhasPorPagina,
        count: contagem,
        filter: filtros.vazio ? undefined : filtros,
        fetch: busca ? getParams(busca, separadorLinhaBusca) : undefined
      }
    }

    Object.keys(params.params).forEach(
      (key) => params.params[key] === undefined && delete params.params[key]
    )

    return requestResolver(
      api_gateway.get(`/api/v2/embeddedSim/requests`, params)
    )
  },
  getESimsFilter: (body) => {
    return requestResolver(
      api_gateway.post(`/api/v2/embeddedSim/fetchEmbeddedSimFilter`, body)
    )
  },
  getProfilesFilter: () => {
    return requestResolver(
      api_gateway.get(`/api/v2/embeddedSim/fetchInventoryFilters`)
    )
  },
  getRequestsOptions: () => {
    return requestResolver(
      api_gateway.get(`/api/v2/embeddedSim/requests/options`)
    )
  },
  getProfilesInfo: async (eidDevice) => {
    return requestResolver(
      api_gateway.get(`/api/v2/embeddedSim/${eidDevice}/info`)
    )
  },
  getRequestsInfo: async (
    solicitationID,
    pagina,
    linhasPorPagina,
    contagem, 
    isImport = false
  ) => {
    const params = {
      params: {
        page: pagina,
        pageRows: linhasPorPagina,
        count: contagem,
        requestId: solicitationID,
        isImport: isImport
      }
    }

    return requestResolver(
      api_gateway.get(`/api/v2/embeddedSim/requests/details`, params)
    )
  },
  getProfilesOnDevices: (ids, filters = {}) => {
    const params = { params: { embeddedSimId: ids.join(',') } }

    const finalFiltros = transformData(filters)
    if (Object.keys(finalFiltros).length > 0) {
      params.params.filter = finalFiltros
    }

    return requestResolver(
      api_gateway.get(`/api/v2/embeddedSim/profile/list`, params)
    )
  },

  getHasESimOnDevices: () => {
    return requestResolver(api_gateway.get(`/api/v2/devices/esimOnDevices`))
  },

  getOffers: async (carriersIds = []) => {
    const params = {
      params: {
        ...(carriersIds.length > 0 && { carriersIds: carriersIds.join(',') })
      }
    }
    return requestResolver(
      api_gateway.get(`/api/v2/embeddedSim/offer/list`, params)
    )
  },

  getOfferAndCarrier: () => {
    return requestResolver(
      api_gateway.get(`/api/v2/embeddedSim/offerAndCarrier`)
    )
  },

  getOffersFees: (offerIds, { signal } = {}) => {
    const params = {
      params: { offerIds: offerIds.join(',') },
      signal
    }

    return requestResolver(
      api_gateway.get(`/api/v2/embeddedSim/offer/fees`, params)
    )
  },
  getCancellationFee: (embeddedSimIds) => {
    const ids = Array.isArray(embeddedSimIds)
      ? embeddedSimIds.filter(Boolean).join(',')
      : String(embeddedSimIds ?? '')

    return requestResolver(
      api_gateway.get(`/api/v2/embeddedSim/cancellationFee?id=${ids}`)
    )
  },
  //#endregion GET

  //#region POST
  activateBootstrap: (embeddedSimIds, offerId) => {
    const params = {
      offerId: Number(offerId),
      embeddedSimIds: embeddedSimIds
    }

    return requestResolver(
      api_gateway.post(`/api/v2/embeddedSim/bootstrap/activate`, params)
    )
  },
  createESimsReport: ({
    filter = {},
    fetch,
    separadorLinhaBusca,
    reportType,
    eSimsIds
  }) => {
    const auxFilter =
      filter.vazio || Object.keys(filter).length === 0
        ? undefined
        : transformData(filter)

    const params = {
      reportType,
      eSimsIds,
      filter: auxFilter,
      fetch: fetch ? getParams(fetch, separadorLinhaBusca) : undefined
    }

    Object.keys(params).forEach(
      (key) => params[key] === undefined && delete params[key]
    )

    return requestResolver(
      api_gateway.post(`/api/v2/embeddedSim/exportData`, params)
    )
  },
  createDetailESimsReport: ({
    filter = {},
    fetch,
    separadorLinhaBusca,
    reportType,
    eSimsIds
  }) => {
    const auxFilter =
      filter.vazio || Object.keys(filter).length === 0
        ? undefined
        : transformData(filter)

    const params = {
      reportType,
      eSimsIds,
      filter: auxFilter,
      fetch: fetch ? getParams(fetch, separadorLinhaBusca) : undefined
    }

    Object.keys(params).forEach(
      (key) => params[key] === undefined && delete params[key]
    )

    return requestResolver(
      api_gateway.post(`/api/v2/embeddedSim/exportData/ESimsDetails`, params)
    )
  },
  createProfilesReport: ({
    filter = {},
    fetch,
    separadorLinhaBusca,
    reportType,
    profilesIds
  }) => {
    const auxFilter =
      filter.vazio || Object.keys(filter).length === 0
        ? undefined
        : transformData(filter)

    const params = {
      reportType,
      profilesIds,
      filter: auxFilter,
      fetch: fetch ? getParams(fetch, separadorLinhaBusca) : undefined
    }

    Object.keys(params).forEach(
      (key) => params[key] === undefined && delete params[key]
    )

    return requestResolver(
      api_gateway.post(`/api/v2/embeddedSim/exportData/profiles`, params)
    )
  },
  createSolicitationsReport: ({
    filter = {},
    fetch,
    separadorLinhaBusca,
    reportType,
    requestIds
  }) => {
    const auxFilter =
      filter.vazio || Object.keys(filter).length === 0
        ? undefined
        : transformData(filter)

    const params = {
      reportType,
      requestIds,
      filter: auxFilter,
      fetch: fetch ? getParams(fetch, separadorLinhaBusca) : undefined
    }

    Object.keys(params).forEach(
      (key) => params[key] === undefined && delete params[key]
    )

    return requestResolver(
      api_gateway.post(`/api/v2/embeddedSim/exportData/requests`, params)
    )
  },
  createDetailSolicitationsReport: ({
    filter = {},
    fetch,
    separadorLinhaBusca,
    reportType,
    requestIds
  }) => {
    const auxFilter =
      filter.vazio || Object.keys(filter).length === 0
        ? undefined
        : transformData(filter)

    const params = {
      reportType,
      requestIds,
      filter: auxFilter,
      fetch: fetch ? getParams(fetch, separadorLinhaBusca) : undefined
    }

    Object.keys(params).forEach(
      (key) => params[key] === undefined && delete params[key]
    )

    return requestResolver(
      api_gateway.post(`/api/v2/embeddedSim/exportData/requestDetails`, params)
    )
  },
  /**
   * Importa e-SIMs a partir de arquivo
   * @param {Object} params
   * @param {string} params.fileName - Nome do arquivo no S3
   * @param {boolean} params.activation - Ativação (obrigatório)
   * @param {string|number} [params.carrier] - ID da operadora (opcional)
   * @param {string|number} [params.offer] - ID da oferta (opcional)
   */
  inputESims: (fileName) => {
    return requestResolver(
      api_gateway.post('/api/v2/embeddedSim/input', {
        fileName: fileName
      })
    )
  },
  inputProfile: (fileName) => {
    return requestResolver(
      api_gateway.post('/api/v2/embeddedSim/inputProfile', {
        fileName: fileName
      })
    )
  },
  disableFactoryProfiles: (embeddedSimIds) => {
    return requestResolver(
      api_gateway.post(`/api/v2/embeddedSim/profile/disableFactoryProfile`, {
        embeddedSimIds
      })
    )
  },
  cancelEsims: (embeddedSimIds) => {
    return requestResolver(
      api_gateway.post(`/api/v2/embeddedSim/cancel`, {
        embeddedSimIds
      })
    )
  },
  //#endregion POST

  //#region UPDATE
  downloadProfiles: (offerId, embeddedSimIds) => {
    const params = {
      offerId,
      embeddedSimId: embeddedSimIds
    }

    return requestResolver(
      api_gateway.put(`/api/v2/embeddedSim/profile/download`, params)
    )
  },
  updateESim: (cdESim, device) => {
    const params = {
      embeddedSim: device
    }

    return api_gateway.put(`/api/v2/embeddedSim/${cdESim}`, params)
  },
  saveFavActions: (manejador, fav) => {
    return requestResolver(
      api_gateway.put('/api/v2/favorites/add', {
        acao: manejador,
        favoritas: fav
      })
    )
  },
  //#endregion UPDATE

  //#region PATCH
  changeOffer: (offersPayload, subscriberBase = false) => {
    const offers = (offersPayload || [])
      .map((item) => {
        const offerId = Number(item?.offerId ?? item?.offer)
        const embeddedSimId = item?.embeddedSimId ?? item?.ids

        return {
          offerId,
          embeddedSimId: Array.isArray(embeddedSimId) ? embeddedSimId : []
        }
      })
      .filter((o) => !Number.isNaN(o.offerId))

    const body = subscriberBase ? { subscriberBase: true, offers } : offers

    return requestResolver(
      api_gateway.patch(`/api/v2/embeddedSim/offer/change`, body)
    )
  },
  activateProfile: (profilesIds) => {
    const params = {
      profileId: profilesIds
    }

    return api_gateway.patch(`/api/v2/embeddedSim/profile/activate`, params)
  },
  //#endregion PATCH

  //#region DELETE
  removeProfiles: (profileIDs) => {
    return requestResolver(
      api_gateway.delete(`/api/v2/embeddedSim/profile/delete`, {
        data: {
          profileId: profileIDs
        }
      })
    )
  }
  //#endregion DELETE
}
